import React, { useCallback } from 'react';

import { Action } from '@reduxjs/toolkit';
import { Session } from '@yandex-video-platform/goloom-sdk';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { batchActions } from 'redux-batched-actions';

import { RootState } from '../../../../../../init-store';
import {
    getIsOneOnOneEnabled,
    selectIsMeetingFullscreenEnabled,
} from '../../../../../shared/features/config/selectors';
import { selectIsHeadlessShellEnabled } from '../../../../../shared/features/flags/selectors';
import {
    getIsVideoShouldBeMirrored,
    getShowMyVideo,
    selectHideParticipantsVideo,
} from '../../../../../shared/features/settings';
import { isMobileUa } from '../../../../../shared/features/uatraits';
import { useActions } from '../../../../../shared/hooks';
import { GRID_VIEW } from '../../../constants';
import { selectIsFullscreen } from '../../../features/fullscreen';
import {
    shouldHideVideoGridInFullscreen,
    shouldShowMeetingFullscreenButton,
} from '../../../features/fullscreen/helpers';
import {
    nextGridPageAction,
    prevGridPageAction,
    setCurrentGridPageAction,
    setMaxDisplayParticipants,
    setVideoContainerSize,
} from '../../../features/goloom-video-grid';
import {
    getActiveGridView,
    getCallee,
    getCallStatus,
    getCurrentPage,
    getDesktopSharingStatus,
    getGridItemRatio,
    getIsWaitingForCallee,
    getMaxDisplayParticipants,
    getParticipantsCount,
    getSidebarContent,
    selectEffectiveSlotsGridView,
    selectIsJoined,
    selectIsNewGrid,
    selectMaxSlotsCount,
    selectShouldRenderInviteBlock,
    selectShowNextSlotsPageButton,
    selectShowPrevSlotsPageButton,
} from '../../../selectors';
import { calcMaxDisplayParticipants } from '../helpers/calc-max-display-participants';
import { SlotsRenderer } from './slots-renderer';

interface Props {
    session: Session;
}

const mapStateToProps = (state: RootState) => {
    const desktopSharingStatus = getDesktopSharingStatus(state);
    const isFullscreen = selectIsFullscreen(state);
    const isMeetingFullscreenEnabled = selectIsMeetingFullscreenEnabled(state);
    const shouldHideVideoGrid = shouldHideVideoGridInFullscreen({
        isFullscreen,
        isMeetingFullscreenEnabled,
    });
    const props = {
        maxDisplayParticipants: getMaxDisplayParticipants(state),
        currentPage: getCurrentPage(state),
        activeGridView: getActiveGridView(state),
        itemRatio: getGridItemRatio(state),
        showMyVideo: getShowMyVideo(state),
        participantCount: getParticipantsCount(state),
        isMeScreenSharing: desktopSharingStatus === 'local',
        isMirrorVideo: getIsVideoShouldBeMirrored(state),
        isScreenSharingActive: desktopSharingStatus !== 'none',
        shouldHideVideoGrid,
    };
    if (shouldHideVideoGrid) {
        props.maxDisplayParticipants = 0;
        props.showMyVideo = false;
    }
    return props;
};

export const GoloomVideoGrid: React.FC<Props> = ({ session }) => {
    const {
        maxDisplayParticipants,
        currentPage,
        showMyVideo,
        activeGridView,
        itemRatio,
        participantCount,
        isMeScreenSharing,
        isMirrorVideo,
        isScreenSharingActive,
        shouldHideVideoGrid,
    } = useSelector(mapStateToProps, shallowEqual);
    const dispatch = useDispatch();

    const {
        nextGridPageAction: handleNextPage,
        prevGridPageAction: handlePrevPage,
        setContainerSize,
    } = useActions({
        nextGridPageAction,
        prevGridPageAction,
        setContainerSize: setVideoContainerSize,
    });

    const needShowMyVideo = showMyVideo || participantCount <= 1;
    const needShowSelfScreenSharing = isMeScreenSharing && activeGridView === GRID_VIEW.PRESENTER;
    const shouldRenderInviteBlock = useSelector(selectShouldRenderInviteBlock);

    const oneOnOne = useSelector(getIsOneOnOneEnabled);
    const callee = useSelector(getCallee);
    const isWaitingForCallee = useSelector(getIsWaitingForCallee);
    const callStatus = useSelector(getCallStatus);
    const isNewGrid = useSelector(selectIsNewGrid);
    const isMobile = useSelector(isMobileUa);
    const effectiveGridView = useSelector(selectEffectiveSlotsGridView);
    const isHeadlessShellEnabled = useSelector(selectIsHeadlessShellEnabled);
    const showPrevPageButton = useSelector(selectShowPrevSlotsPageButton);
    const showNextPageButton = useSelector(selectShowNextSlotsPageButton);
    const maxSlotsCount = useSelector(selectMaxSlotsCount);
    const isMeetingFullscreenEnabled = useSelector(selectIsMeetingFullscreenEnabled);
    const isJoined = useSelector(selectIsJoined);
    const sidebarContent = useSelector(getSidebarContent);
    const showMeetingFullscreenButton = shouldShowMeetingFullscreenButton({
        isMeetingFullscreenEnabled,
        isJoined,
        isScreenSharingActive,
    });

    const changeMaxDisplayParticipants = useCallback(
        (elem?: Element) => {
            if (activeGridView === GRID_VIEW.PRESENTER && !shouldHideVideoGrid) {
                const count = calcMaxDisplayParticipants({
                    containerWidth: elem?.clientWidth,
                    containerHeight: elem?.clientHeight,
                    isNewGrid,
                    isMobile,
                });

                if (count !== maxDisplayParticipants) {
                    dispatch(batchActions([setMaxDisplayParticipants(count), setCurrentGridPageAction(0)]) as Action);
                }
            }
        },
        [dispatch, activeGridView, maxDisplayParticipants, shouldHideVideoGrid, isNewGrid, isMobile]
    );

    const onResize = useCallback(
        (elem?: Element) => {
            if (elem) {
                setContainerSize({ width: elem.clientWidth, height: elem.clientHeight });
            }
            changeMaxDisplayParticipants(elem);
        },
        [changeMaxDisplayParticipants, setContainerSize]
    );

    const hideParticipantsVideo = useSelector(selectHideParticipantsVideo);

    return (
        <SlotsRenderer
            session={session}
            gridView={effectiveGridView}
            itemRatio={itemRatio}
            shouldRenderInviteBlock={shouldRenderInviteBlock}
            maxDisplayParticipants={maxDisplayParticipants}
            currentPage={currentPage}
            needShowMyVideo={needShowMyVideo}
            needShowSelfScreenSharing={needShowSelfScreenSharing}
            onNextPageClick={handleNextPage}
            onPrevPageClick={handlePrevPage}
            isMirrorVideo={isMirrorVideo}
            isScreenSharingActive={isScreenSharingActive}
            onResize={onResize}
            isOneOnOne={oneOnOne}
            callee={callee}
            isWaitingForCallee={isWaitingForCallee}
            callStatus={callStatus}
            hideParticipantsVideo={hideParticipantsVideo}
            isNewGrid={isNewGrid}
            showPrevPageButton={showPrevPageButton}
            showNextPageButton={showNextPageButton}
            maxSlotsCount={maxSlotsCount}
            isShellEnabled={isHeadlessShellEnabled}
            showMeetingFullscreenButton={showMeetingFullscreenButton}
            pinMeetingFullscreenButton={!sidebarContent}
        />
    );
};
